import React, { useState } from 'react';
import { useSchema } from '../../../contexts/SchemaContext';
import { createIndicatorCondition, createCompositeCondition, createCandlestickCondition } from '../../../utils/strategyUtils';
import IndicatorConditionBuilder from './IndicatorConditionBuilder';
import CompositeConditionBuilder from './CompositeConditionBuilder';
import CandlestickConditionBuilder from './CandlestickConditionBuilder';

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const AddIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

const ChevronUpIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
);

const ArrowDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
);

/**
 * Inline builder for a single condition (initial or chain link)
 */
const InlineConditionEditor = ({
    condition,
    onChange,
    onSelectType,
    disabled,
    rulesSchema,
    indicatorSchemas,
    expanded,
    onToggleExpand,
    label,
}) => {
    const { advancedRulesInfo } = useSchema();

    if (!condition) {
        // Show type selection
        return (
            <div className="py-3 text-center bg-base-200 rounded-lg border border-dashed border-base-300">
                <p className="text-base-content/60 text-sm mb-2">{label}</p>
                <div className="flex gap-2 justify-center flex-wrap">
                    <button className="btn btn-xs btn-outline" onClick={() => onSelectType('INDICATOR')} disabled={disabled}>
                        Indicator
                    </button>
                    {advancedRulesInfo?.compositeSupported && (
                        <button className="btn btn-xs btn-outline" onClick={() => onSelectType('COMPOSITE')} disabled={disabled}>
                            Composite
                        </button>
                    )}
                    {advancedRulesInfo?.candlestickSupported && (
                        <button className="btn btn-xs btn-outline" onClick={() => onSelectType('CANDLESTICK')} disabled={disabled}>
                            Candlestick
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const getConditionSummary = (cond) => {
        if (cond.conditionType === 'INDICATOR') return `${cond.indicatorType || '?'} ${cond.operator || ''}`;
        if (cond.conditionType === 'COMPOSITE') {
            const left = cond.leftIndicator?.type || cond.leftIndicator?.indicatorType || '?';
            const right = cond.rightIndicator?.type || cond.rightIndicator?.indicatorType || '?';
            return `${left} vs ${right}`;
        }
        if (cond.conditionType === 'CANDLESTICK') return cond.patternType || 'Pattern';
        return cond.conditionType;
    };

    return (
        <div className="card card-border bg-base-100">
            <div
                className="flex items-center gap-2 px-3 py-2 bg-base-200 cursor-pointer"
                onClick={onToggleExpand}
            >
                <button className="btn btn-ghost btn-xs btn-circle">
                    {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </button>
                <span className="badge badge-sm badge-primary badge-outline">
                    {condition.conditionType}
                </span>
                <span className="text-sm flex-1 truncate text-base-content/70">
                    {getConditionSummary(condition)}
                </span>
            </div>
            {expanded && (
                <div className="p-3 border-t border-base-200">
                    {condition.conditionType === 'INDICATOR' && (
                        <IndicatorConditionBuilder
                            condition={condition}
                            onChange={onChange}
                            errors={{}}
                            disabled={disabled}
                            rulesSchema={rulesSchema}
                            indicatorSchemas={indicatorSchemas}
                        />
                    )}
                    {condition.conditionType === 'COMPOSITE' && (
                        <CompositeConditionBuilder
                            condition={condition}
                            onChange={onChange}
                            errors={{}}
                            disabled={disabled}
                            rulesSchema={rulesSchema}
                            indicatorSchemas={indicatorSchemas}
                        />
                    )}
                    {condition.conditionType === 'CANDLESTICK' && (
                        <CandlestickConditionBuilder
                            condition={condition}
                            onChange={onChange}
                            disabled={disabled}
                            rulesSchema={rulesSchema}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * ChainConditionBuilder - Builder for sequential pattern conditions
 *
 * @param {Object} props
 * @param {Object} props.condition - Current chain condition data
 * @param {Function} props.onChange - Change handler (updatedCondition) => void
 * @param {boolean} props.disabled - Whether form is disabled
 * @param {Object} props.rulesSchema - Rules schema from API
 * @param {Object} props.indicatorSchemas - Indicator schemas from API
 */
const ChainConditionBuilder = ({
    condition,
    onChange,
    disabled = false,
    rulesSchema = null,
    indicatorSchemas = null,
}) => {
    const { advancedRulesInfo } = useSchema();
    const [expandedSection, setExpandedSection] = useState('initial'); // 'initial' | link index | null

    const initialCondition = condition?.initialCondition || null;
    const chainLinks = condition?.chainLinks || [];

    const createConditionForType = (type) => {
        if (type === 'INDICATOR') return createIndicatorCondition();
        if (type === 'COMPOSITE') return createCompositeCondition();
        if (type === 'CANDLESTICK') return createCandlestickCondition();
        return null;
    };

    const handleSetInitialCondition = (type) => {
        onChange({
            ...condition,
            initialCondition: createConditionForType(type),
        });
        setExpandedSection('initial');
    };

    const handleInitialConditionChange = (updated) => {
        onChange({ ...condition, initialCondition: updated });
    };

    const handleClearInitialCondition = () => {
        onChange({ ...condition, initialCondition: null });
    };

    const handleAddChainLink = (type) => {
        const newLink = {
            condition: createConditionForType(type),
            threshold: 5,
        };
        const newLinks = [...chainLinks, newLink];
        onChange({ ...condition, chainLinks: newLinks });
        setExpandedSection(chainLinks.length); // expand newly added
    };

    const handleChainLinkConditionChange = (idx, updatedCond) => {
        const newLinks = [...chainLinks];
        newLinks[idx] = { ...newLinks[idx], condition: updatedCond };
        onChange({ ...condition, chainLinks: newLinks });
    };

    const handleChainLinkThresholdChange = (idx, value) => {
        const newLinks = [...chainLinks];
        newLinks[idx] = { ...newLinks[idx], threshold: Math.max(1, parseInt(value, 10) || 1) };
        onChange({ ...condition, chainLinks: newLinks });
    };

    const handleDeleteChainLink = (idx) => {
        const newLinks = chainLinks.filter((_, i) => i !== idx);
        onChange({ ...condition, chainLinks: newLinks });
        if (expandedSection === idx) setExpandedSection(null);
        else if (typeof expandedSection === 'number' && expandedSection > idx) {
            setExpandedSection(expandedSection - 1);
        }
    };

    return (
        <div className="space-y-4">
            <h4 className="text-sm font-medium text-base-content/70">
                Chain Condition (Sequential Pattern)
            </h4>
            <div className="divider my-1"></div>

            {/* Info alert */}
            <div className="alert alert-info text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>Conditions must occur <strong>in sequence</strong>. Each link must trigger within its bar threshold after the previous one.</span>
            </div>

            {/* Initial Condition */}
            <div>
                <label className="label">
                    <span className="label-text font-medium">Initial Condition</span>
                    <span className="label-text-alt text-base-content/50">First condition in the chain</span>
                </label>

                {initialCondition ? (
                    <div className="relative">
                        <InlineConditionEditor
                            condition={initialCondition}
                            onChange={handleInitialConditionChange}
                            disabled={disabled}
                            rulesSchema={rulesSchema}
                            indicatorSchemas={indicatorSchemas}
                            expanded={expandedSection === 'initial'}
                            onToggleExpand={() => setExpandedSection(expandedSection === 'initial' ? null : 'initial')}
                        />
                        <button
                            className="btn btn-ghost btn-xs btn-circle text-error absolute top-2 right-2"
                            onClick={handleClearInitialCondition}
                            disabled={disabled}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                ) : (
                    <InlineConditionEditor
                        condition={null}
                        onSelectType={handleSetInitialCondition}
                        disabled={disabled}
                        label="Select initial condition type"
                    />
                )}
            </div>

            {/* Chain Links */}
            {chainLinks.length > 0 && (
                <div className="space-y-3">
                    {chainLinks.map((link, idx) => (
                        <div key={idx}>
                            {/* Arrow connector */}
                            <div className="flex items-center justify-center py-1 text-base-content/30">
                                <ArrowDownIcon />
                                <span className="text-xs ml-1">within {link.threshold} bar{link.threshold !== 1 ? 's' : ''}</span>
                            </div>

                            {/* Chain link card */}
                            <div className="relative border-l-4 border-l-warning pl-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="badge badge-sm badge-warning">Link {idx + 1}</span>
                                    <div className="form-control flex-row items-center gap-1">
                                        <span className="text-xs text-base-content/60">Bars:</span>
                                        <input
                                            type="number"
                                            className="input input-bordered input-xs w-16"
                                            value={link.threshold}
                                            onChange={(e) => handleChainLinkThresholdChange(idx, e.target.value)}
                                            min={1}
                                            max={100}
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div className="flex-1"></div>
                                    <button
                                        className="btn btn-ghost btn-xs btn-circle text-error"
                                        onClick={() => handleDeleteChainLink(idx)}
                                        disabled={disabled}
                                    >
                                        <DeleteIcon />
                                    </button>
                                </div>

                                <InlineConditionEditor
                                    condition={link.condition}
                                    onChange={(updated) => handleChainLinkConditionChange(idx, updated)}
                                    onSelectType={(type) => {
                                        handleChainLinkConditionChange(idx, createConditionForType(type));
                                    }}
                                    disabled={disabled}
                                    rulesSchema={rulesSchema}
                                    indicatorSchemas={indicatorSchemas}
                                    expanded={expandedSection === idx}
                                    onToggleExpand={() => setExpandedSection(expandedSection === idx ? null : idx)}
                                    label="Select chain link condition type"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add chain link dropdown */}
            <div className="dropdown dropdown-top">
                <div tabIndex={0} role="button" className="btn btn-sm btn-outline gap-1" disabled={disabled}>
                    <AddIcon /> Add Chain Link
                </div>
                <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-48">
                    <li><a onClick={() => handleAddChainLink('INDICATOR')}>Indicator</a></li>
                    {advancedRulesInfo?.compositeSupported && (
                        <li><a onClick={() => handleAddChainLink('COMPOSITE')}>Composite</a></li>
                    )}
                    {advancedRulesInfo?.candlestickSupported && (
                        <li><a onClick={() => handleAddChainLink('CANDLESTICK')}>Candlestick</a></li>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default ChainConditionBuilder;
